<template>
  <Teleport to="body">
    <div v-if="visible && target" :key="sequence" class="archive-spider-overlay" aria-hidden="true">
      <svg class="archive-silk">
        <line :x1="spiderX + 26" :y1="spiderY" :x2="targetX" :y2="targetY" pathLength="1" />
      </svg>
      <div class="archive-spider-run" :style="positionStyle" @animationend.self="visible = false">
        <HalloweenSpiderShape />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { halloweenArchiveTarget as target } from "@/functions/halloweenSpider";
import isModifierSet from "@/functions/userModifier";
import { ModifierType } from "@/functions/userModifierEnum";
import useSound from "@/functions/useSound";
import scuttleSound from "@/assets/soundEffects/spider-scuttle.wav";
import HalloweenSpiderShape from "./HalloweenSpiderShape.vue";

const visible = ref(false);
const sequence = ref(0);
const targetX = computed(() => (target.value?.left ?? 0) + (target.value?.width ?? 0) / 2);
const targetY = computed(() => (target.value?.top ?? 0) + (target.value?.height ?? 0) / 2);
const spiderX = computed(() => Math.max(45, (target.value?.left ?? 0) - 55));
const spiderY = computed(() => Math.max(50, Math.min(window.innerHeight - 50, targetY.value - 55)));
const positionStyle = computed(() => ({
  "--spider-x": `${spiderX.value}px`,
  "--spider-y": `${spiderY.value}px`,
}));
const { play } = useSound(scuttleSound, {
  soundEnabled: () => isModifierSet(ModifierType.enableSFX),
  volume: 0.35,
});
watch(target, () => {
  sequence.value += 1;
  visible.value = true;
  play();
});
</script>

<style scoped lang="scss">
.archive-spider-overlay {
  position: fixed;
  inset: 0;
  z-index: 10001;
  overflow: hidden;
  pointer-events: none;
}

.archive-spider-run {
  position: absolute;
  left: -45px;
  top: -45px;
  width: 90px;
  height: 90px;
  animation: archive-spider-visit 5.2s linear forwards;
}

.archive-silk {
  width: 100%;
  height: 100%;
  overflow: visible;
  stroke: #d5d9e2;
  stroke-width: 2;
  filter: drop-shadow(0 1px 1px #352c4266);

  line {
    stroke-dasharray: 1;
    animation: archive-silk-shot 5.2s linear both;
  }
}

@keyframes archive-spider-visit {
  0% {
    transform: translate(-100px, calc(var(--spider-y) + 80px)) rotate(-20deg);
  }
  20%,
  55% {
    transform: translate(var(--spider-x), var(--spider-y));
  }
  100% {
    transform: translate(calc(100vw + 100px), calc(var(--spider-y) - 60px)) rotate(-10deg);
  }
}

@keyframes archive-silk-shot {
  0%,
  20% {
    opacity: 0;
    stroke-dashoffset: 1;
  }
  21% {
    opacity: 1;
    stroke-dashoffset: 1;
  }
  27%,
  45% {
    opacity: 1;
    stroke-dashoffset: 0;
  }
  55%,
  100% {
    opacity: 0;
    stroke-dashoffset: 0;
  }
}

@media (prefers-reduced-motion: reduce), print {
  .archive-spider-overlay {
    display: none;
  }
}
</style>
